// src/features/authz/DiscoveryPanel.tsx
// Permission discovery: what can a user access, who can access an object,
// and how is a relation computed (expand tree, shown as raw JSON).
// See docs/ux/authz-ia.md section 3.

import { useState } from 'react';
import { useI18n, useAuthz } from '@react/hooks';
import { Button, Input, ErrorAlert } from '@react/components';

function ListObjectsCard() {
  const { t } = useI18n();
  const store = useAuthz();
  const [user, setUser] = useState('user:');
  const [relation, setRelation] = useState('');
  const [type, setType] = useState('');
  const [objects, setObjects] = useState<string[] | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user.trim() || !relation.trim() || !type.trim()) return;
    setIsRunning(true);
    try {
      const r = await store.ListObjects({ user: user.trim(), relation: relation.trim(), type: type.trim(), page_size: 50, continuation_token: '', authorization_model_id: '' });
      setObjects(r.objects?.items ?? []);
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="space-y-2 rounded-lg border border-border p-4">
      <h3 className="text-sm font-semibold text-foreground">{t('authz.discovery.listObjects.title')}</h3>
      <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-3">
        <Input label={t('authz.check.user')} value={user} onChange={(e) => setUser(e.target.value)} className="w-48" required />
        <Input label={t('authz.check.relation')} value={relation} onChange={(e) => setRelation(e.target.value)} className="w-32" required />
        <Input label={t('authz.discovery.listObjects.objectType')} value={type} onChange={(e) => setType(e.target.value)} placeholder="document" className="w-40" required />
        <Button type="submit" size="sm" disabled={isRunning}>{isRunning ? t('common.loading') : t('authz.discovery.run')}</Button>
      </form>
      {objects && (
        objects.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('authz.discovery.listObjects.empty')}</p>
        ) : (
          <ul className="space-y-0.5 font-mono text-xs">
            {objects.map((o) => <li key={o} className="rounded bg-muted/40 px-2 py-1">{o}</li>)}
          </ul>
        )
      )}
    </div>
  );
}

function ListUsersCard() {
  const { t } = useI18n();
  const store = useAuthz();
  const [object, setObject] = useState('');
  const [relation, setRelation] = useState('');
  const [userType, setUserType] = useState('user');
  const [users, setUsers] = useState<{ user_id?: string; userset?: string; wildcard?: string }[] | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!object.trim() || !relation.trim()) return;
    setIsRunning(true);
    try {
      const r = await store.ListUsersByObject({
        object: object.trim(),
        relation: relation.trim(),
        user_filters: { items: [{ type: userType.trim() || 'user', relation: '' }] },
        page_size: 50,
        continuation_token: '',
        authorization_model_id: '',
      });
      setUsers(r.users?.items ?? []);
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="space-y-2 rounded-lg border border-border p-4">
      <h3 className="text-sm font-semibold text-foreground">{t('authz.discovery.listUsers.title')}</h3>
      <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-3">
        <Input label={t('authz.check.object')} value={object} onChange={(e) => setObject(e.target.value)} placeholder="document:readme" className="w-56" required />
        <Input label={t('authz.check.relation')} value={relation} onChange={(e) => setRelation(e.target.value)} className="w-32" required />
        <Input label={t('authz.discovery.listUsers.userType')} value={userType} onChange={(e) => setUserType(e.target.value)} className="w-32" />
        <Button type="submit" size="sm" disabled={isRunning}>{isRunning ? t('common.loading') : t('authz.discovery.run')}</Button>
      </form>
      {users && (
        users.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('authz.discovery.listUsers.empty')}</p>
        ) : (
          <ul className="space-y-0.5 font-mono text-xs">
            {users.map((u, i) => <li key={i} className="rounded bg-muted/40 px-2 py-1">{u.user_id || u.userset || u.wildcard}</li>)}
          </ul>
        )
      )}
    </div>
  );
}

function ExpandCard() {
  const { t } = useI18n();
  const store = useAuthz();
  const [object, setObject] = useState('');
  const [relation, setRelation] = useState('');
  const [tree, setTree] = useState<unknown>(null);
  const [isRunning, setIsRunning] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!object.trim() || !relation.trim()) return;
    setIsRunning(true);
    try {
      const r = await store.Expand({ object: object.trim(), relation: relation.trim(), authorization_model_id: '' });
      setTree(r.tree ?? null);
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="space-y-2 rounded-lg border border-border p-4">
      <h3 className="text-sm font-semibold text-foreground">{t('authz.discovery.expand.title')}</h3>
      <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-3">
        <Input label={t('authz.check.object')} value={object} onChange={(e) => setObject(e.target.value)} placeholder="document:readme" className="w-56" required />
        <Input label={t('authz.check.relation')} value={relation} onChange={(e) => setRelation(e.target.value)} className="w-32" required />
        <Button type="submit" size="sm" disabled={isRunning}>{isRunning ? t('common.loading') : t('authz.discovery.run')}</Button>
      </form>
      {tree != null && (
        <pre className="max-h-80 overflow-auto rounded-md bg-muted/40 p-3 text-xs">{JSON.stringify(tree, null, 2)}</pre>
      )}
    </div>
  );
}

export function DiscoveryPanel() {
  const store = useAuthz();
  return (
    <div className="space-y-4">
      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}
      <ListObjectsCard />
      <ListUsersCard />
      <ExpandCard />
    </div>
  );
}
